import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download } from "lucide-react";
import { hhmm, shiftTime } from "@/components/hrms/AttendanceCard";

const LIVE_TITLES = {
  working: "Working now", on_break: "On break", present: "Present", late: "Late",
  on_leave: "On leave", absent: "Absent", clocked_out: "Logged out", not_started: "Not started",
};

function Empty({ cols, text }) {
  return (
    <TableRow>
      <TableCell colSpan={cols} className="py-8 text-center text-sm text-muted-foreground">{text}</TableCell>
    </TableRow>
  );
}

export function LiveListDialog({ filter, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!filter) return;
    setItems(null);
    api.get("/hrms/live-list", { params: { filter } }).then(({ data }) => setItems(data.items)).catch((e) => toast.error(apiErr(e)));
  }, [filter]);
  const onBreak = filter === "on_break";
  return (
    <Dialog open={!!filter} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="hrms-live-list-dialog">
        <DialogHeader><DialogTitle>{LIVE_TITLES[filter]} {items ? `(${items.length})` : ""}</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Team</TableHead>
                <TableHead>{onBreak ? "Break started" : "Login"}</TableHead>
                <TableHead>{onBreak ? "Break used" : "Worked"}</TableHead>
                <TableHead>{onBreak ? "Break left" : "Status"}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(items || []).map((row) => (
                <TableRow key={row.user_id} data-testid={`hrms-live-row-${row.user_id}`}>
                  <TableCell className="font-medium">{row.employee_name}</TableCell>
                  <TableCell>{row.team_name || "—"}</TableCell>
                  <TableCell>{shiftTime(onBreak ? row.open_break_since : row.login_at)}</TableCell>
                  <TableCell>{onBreak ? `${row.break_minutes} min` : hhmm(row.working_minutes)}</TableCell>
                  <TableCell>{onBreak ? `${row.break_remaining_minutes} min` : <StatusBadge value={row.current_status.replace(/_/g, " ")} />}</TableCell>
                </TableRow>
              ))}
              {items && !items.length && <Empty cols={5} text="Nobody in this list right now." />}
              {!items && <Empty cols={5} text="Loading…" />}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FilterSelect({ value, onChange, options, placeholder, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-44" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{placeholder}</SelectItem>
        {options.map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function usePeopleFilters(rows) {
  const [team, setTeam] = useState("all");
  const [person, setPerson] = useState("all");
  const teams = useMemo(() => [...new Set(rows.map((r) => r.team_name).filter(Boolean))].map((t) => [t, t]), [rows]);
  const people = useMemo(() => [...new Map(rows.map((r) => [r.user_id, r.employee_name])).entries()], [rows]);
  const filtered = rows.filter((r) => (team === "all" || r.team_name === team) && (person === "all" || r.user_id === person));
  const controls = (
    <>
      <FilterSelect value={team} onChange={setTeam} options={teams} placeholder="All teams" testId="hrms-filter-team" />
      <FilterSelect value={person} onChange={setPerson} options={people} placeholder="All employees" testId="hrms-filter-employee" />
    </>
  );
  return { filtered, controls };
}

export function BreakTrackerPanel({ refreshKey }) {
  const [date, setDate] = useState("");
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/hrms/break-tracker", { params: { date: date || undefined } }).then(({ data: d }) => setData(d)).catch((e) => toast.error(apiErr(e)));
  }, [date]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);
  const { filtered, controls } = usePeopleFilters(data?.board || []);
  if (!data) return <Card className="p-5" data-testid="hrms-break-tracker-loading">Loading break tracker…</Card>;
  const c = data.counts || {};
  return (
    <Card className="overflow-hidden" data-testid="hrms-break-tracker">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
        <div>
          <div className="font-display text-lg font-semibold">Break tracker · {data.work_date}</div>
          <div className="text-xs text-muted-foreground">{c.members} members · {c.active} working · {c.on_break} on break · {c.inactive} not active</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="hrms-break-date-filter" />
          {controls}
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead><TableHead>Status</TableHead><TableHead>Breaks (start – end · duration)</TableHead>
              <TableHead>Total</TableHead><TableHead>Remaining</TableHead><TableHead>Flag</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((row) => (
              <TableRow key={row.user_id} data-testid={`hrms-break-row-${row.user_id}`}>
                <TableCell><div className="font-medium">{row.employee_name}</div><div className="text-xs text-muted-foreground">{row.team_name || "—"} · {row.shift_name}</div></TableCell>
                <TableCell><StatusBadge value={row.status.replace(/_/g, " ")} /></TableCell>
                <TableCell className="text-xs">
                  {(row.breaks || []).map((b, i) => (
                    <div key={b.start_at}>#{i + 1} {shiftTime(b.start_at)} – {b.end_at ? shiftTime(b.end_at) : "now"} · {b.duration_minutes ?? 0} min</div>
                  ))}
                  {!row.breaks?.length && "—"}
                </TableCell>
                <TableCell>{row.break_minutes} min · {row.break_count}/{row.breaks_allowed}</TableCell>
                <TableCell>{row.break_remaining_minutes} min</TableCell>
                <TableCell>{row.break_over_limit ? <span className="font-semibold text-red-600" data-testid={`hrms-break-flag-${row.user_id}`}>Over limit</span> : "—"}</TableCell>
              </TableRow>
            ))}
            {!filtered.length && <Empty cols={6} text="No members in this view." />}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function TotalsTable({ rows, title, testId }) {
  return (
    <Card className="overflow-hidden" data-testid={testId}>
      <div className="border-b border-border px-5 py-3 font-semibold">{title}</div>
      <div className="max-h-72 overflow-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Employee</TableHead><TableHead>Days</TableHead><TableHead>Worked</TableHead><TableHead>Break</TableHead><TableHead>Overtime</TableHead><TableHead>Late days</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={`${r.period}-${r.user_id}`}>
                <TableCell>{r.period}</TableCell><TableCell>{r.employee_name}</TableCell><TableCell>{r.days}</TableCell>
                <TableCell>{hhmm(r.worked_minutes)}</TableCell><TableCell>{hhmm(r.break_minutes)}</TableCell>
                <TableCell>{hhmm(r.overtime_minutes)}</TableCell><TableCell>{r.late_days}</TableCell>
              </TableRow>
            ))}
            {!rows.length && <Empty cols={7} text="No totals for this range." />}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

export function WorkingHoursPanel({ refreshKey }) {
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const params = { start: range.start || undefined, end: range.end || undefined };
  const load = useCallback(() => {
    api.get("/hrms/reports/shift-days", { params: { start: range.start || undefined, end: range.end || undefined } })
      .then(({ data: d }) => setData(d)).catch((e) => toast.error(apiErr(e)));
  }, [range]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const { filtered, controls } = usePeopleFilters(data?.rows || []);
  const exportCsv = async () => {
    try {
      const response = await api.get("/hrms/reports/shift-days/csv", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "nexreq-working-hours.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };
  if (!data) return <Card className="p-5" data-testid="hrms-working-hours-loading">Loading working hours…</Card>;
  return (
    <div className="space-y-4" data-testid="hrms-working-hours">
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div className="font-display text-lg font-semibold">Working hours by shift day</div>
          <div className="flex flex-wrap gap-2">
            <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="w-40" data-testid="hrms-wh-start" />
            <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="w-40" data-testid="hrms-wh-end" />
            {controls}
            <Button variant="outline" onClick={exportCsv} data-testid="hrms-wh-export-csv"><Download className="mr-1.5 h-4 w-4" /> CSV / Excel</Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Shift day</TableHead><TableHead>Employee</TableHead><TableHead>Login</TableHead><TableHead>Logout</TableHead>
                <TableHead>Worked</TableHead><TableHead>Break</TableHead><TableHead>Net hours</TableHead><TableHead>Overtime</TableHead><TableHead>Late</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`hrms-wh-row-${r.id}`}>
                  <TableCell>{r.work_date}</TableCell>
                  <TableCell><div className="font-medium">{r.employee_name}</div><div className="text-xs text-muted-foreground">{r.shift_name} · {r.timezone_label}</div></TableCell>
                  <TableCell>{shiftTime(r.login_at, r.timezone)}</TableCell>
                  <TableCell>{r.logout_at ? shiftTime(r.logout_at, r.timezone) : <StatusBadge value={r.status.replace(/_/g, " ")} />}</TableCell>
                  <TableCell>{hhmm(r.worked_minutes)}</TableCell>
                  <TableCell>{hhmm(r.break_minutes)}</TableCell>
                  <TableCell>{(r.worked_minutes / 60).toFixed(2)}</TableCell>
                  <TableCell>{hhmm(r.overtime_minutes)}</TableCell>
                  <TableCell>{r.late ? <span className="text-amber-600">{r.late_minutes} min</span> : "—"}</TableCell>
                </TableRow>
              ))}
              {!filtered.length && <Empty cols={9} text="No shift days in this range." />}
            </TableBody>
          </Table>
        </div>
      </Card>
      <div className="grid gap-4 xl:grid-cols-2">
        <TotalsTable rows={data.weekly} title="Weekly totals" testId="hrms-wh-weekly" />
        <TotalsTable rows={data.monthly} title="Monthly totals" testId="hrms-wh-monthly" />
      </div>
    </div>
  );
}
